import {
  deriveLegacyRole,
  isPermissionKey,
  isRole,
  normalizeScopeEntries,
  systemRole,
  type PermissionKey,
  type Role,
  type ScopeEntry,
} from '@nexa/auth';
import { expandScopes } from '@nexa/airport-reference';
import type { IMeAccessRole, OperatorMe } from '@/lib/me/api';

// Pure access resolution for the operator console (users-roles-permissions R5).
//
// One question, answered in one place: what may this operator see? The BFF answers it in
// `GET /v1/me@v2`; this module re-validates that answer (it is wire data) and FAILS CLOSED
// on anything it cannot read — an unknown permission is dropped, a malformed scope means
// no airports, a malformed `airports` means none. Only two fallbacks exist, both explicit:
//
//   - an older BFF (no `access` block) → the legacy role's system-role permissions;
//   - the offline dev provider (`VITE_AUTH0_DISABLE`) → the role in `VITE_DEV_ROLE`.

/** Where the snapshot came from. `dev` = the offline mock provider. */
export type AccessSource = 'store' | 'legacy' | 'dev';

/** Everything the console needs to gate itself. Immutable snapshot. */
export interface IAccessSnapshot {
  roles: readonly IMeAccessRole[];
  roleKeys: readonly string[];
  /** Effective permissions, catalog keys only. */
  permissions: readonly PermissionKey[];
  /** The scope list; tenant-wide is `[{ kind: 'ALL' }]`, none is `[]`. */
  scope: readonly ScopeEntry[];
  /** Effective airports: `'*'` = the airline's whole operating set, `[]` = none. */
  airports: readonly string[] | '*';
  noAccess: boolean;
  accessVersion: number;
  source: AccessSource;
  /** The legacy role today's `RoleGate` / `hasRole` call sites evaluate (M1). */
  legacyRole: Role | null;
}

/** The empty, fail-closed snapshot: signed in, nothing granted. */
export const NO_ACCESS: IAccessSnapshot = Object.freeze({
  roles: [],
  roleKeys: [],
  permissions: [],
  scope: [],
  airports: [],
  noAccess: true,
  accessVersion: 0,
  source: 'legacy',
  legacyRole: null,
});

/**
 * Effective airports of a scope list, without the tenant's operating set (the BFF owns
 * that intersection): `null` (absent) → `'*'` tenant-wide, `[]` → none, entries → their
 * union. Anything else → none.
 */
export function airportsOfScope(scope: readonly ScopeEntry[] | null): readonly string[] | '*' {
  if (scope === null) return '*';
  return expandScopes(scope);
}

/** Tenant-wide is spelled `[{ kind: 'ALL' }]` in a snapshot; absence never survives. */
function scopeList(scope: readonly ScopeEntry[] | null): readonly ScopeEntry[] {
  return scope === null ? [{ kind: 'ALL' }] : scope;
}

/**
 * The access a single SYSTEM role grants, scoped by `scope` (`null` = tenant-wide).
 * An unknown or absent role key is no access — never a default role.
 */
export function accessOfSystemRole(
  roleKey: string | null,
  scope: readonly ScopeEntry[] | null,
  source: AccessSource,
): IAccessSnapshot {
  const role = roleKey ? systemRole(roleKey) : undefined;
  if (!role) return { ...NO_ACCESS, source };
  return {
    roles: [{ key: role.key, name: role.name, system: true }],
    roleKeys: [role.key],
    permissions: [...role.permissions],
    scope: scopeList(scope),
    airports: airportsOfScope(scope),
    noAccess: false,
    accessVersion: 0,
    source,
    legacyRole: deriveLegacyRole([role.baseTier]),
  };
}

function parseRoles(raw: unknown): IMeAccessRole[] {
  if (!Array.isArray(raw)) return [];
  const out: IMeAccessRole[] = [];
  for (const r of raw as unknown[]) {
    if (!r || typeof r !== 'object') continue;
    const { key, name, system } = r as Record<string, unknown>;
    if (typeof key !== 'string' || !key) continue;
    out.push({ key, name: typeof name === 'string' && name ? name : key, system: system === true });
  }
  return out;
}

const IATA = /^[A-Z]{3}$/;

function parseAirports(raw: unknown): readonly string[] | '*' {
  if (raw === '*') return '*';
  if (!Array.isArray(raw)) return [];
  const codes = new Set<string>();
  for (const c of raw as unknown[]) {
    const code = typeof c === 'string' ? c.trim().toUpperCase() : '';
    // One junk element poisons the list — the same rule `normalizeScopeEntries` applies.
    if (!IATA.test(code)) return [];
    codes.add(code);
  }
  return [...codes].sort();
}

/**
 * Validate the `access` block of `GET /v1/me@v2`. Fail closed throughout: unknown
 * permission keys are dropped, a malformed `scope` / `airports` means no airports, and a
 * missing `noAccess` flag is inferred from the permissions (none ⇒ no access).
 */
export function parseWireAccess(raw: unknown, role: unknown): IAccessSnapshot {
  if (!raw || typeof raw !== 'object') return NO_ACCESS;
  const a = raw as Record<string, unknown>;
  const permissions = Array.isArray(a.permissions)
    ? [...new Set((a.permissions as unknown[]).filter(isPermissionKey))]
    : [];
  const noAccess = typeof a.noAccess === 'boolean' ? a.noAccess : permissions.length === 0;
  const roleKeys = Array.isArray(a.roleKeys)
    ? (a.roleKeys as unknown[]).filter((k): k is string => typeof k === 'string')
    : [];
  const source: AccessSource = a.source === 'store' ? 'store' : 'legacy';
  if (noAccess) return { ...NO_ACCESS, roleKeys, source };
  return {
    roles: parseRoles(a.roles),
    roleKeys,
    permissions,
    scope: normalizeScopeEntries(a.scope) ?? [],
    airports: parseAirports(a.airports),
    noAccess: false,
    accessVersion:
      typeof a.accessVersion === 'number' && Number.isFinite(a.accessVersion) ? a.accessVersion : 0,
    source,
    legacyRole: isRole(role) ? role : null,
  };
}

/**
 * The snapshot for a `GET /v1/me` response. `/v1/me@v2` carries `access`; an older BFF
 * does not, and its `role` maps 1:1 onto the system role of the same key (brief §4.17),
 * scoped by the token's scope claim (`claimScope`, already normalized; `null` = absent).
 */
export function accessOfMe(
  me: OperatorMe,
  claimScope: readonly ScopeEntry[] | null,
): IAccessSnapshot {
  if (me.access !== undefined) return parseWireAccess(me.access, me.role);
  const role = isRole(me.role) ? me.role : null;
  return accessOfSystemRole(role, claimScope, 'legacy');
}

export interface IResolveInput {
  /** `GET /v1/me`, when it has loaded. */
  me: OperatorMe | undefined;
  /** The token's scope claim through `normalizeScopeEntries` (`null` = absent). */
  claimScope: readonly ScopeEntry[] | null;
  /** The offline dev provider's access, when it is mounted. */
  dev: IAccessSnapshot | null;
}

/**
 * The effective snapshot, or `null` while nothing authoritative is known yet.
 *
 * Under the offline dev provider the BFF's answer describes ITS bypass operator, not the
 * role the developer picked in `VITE_DEV_ROLE` — so it only wins when it is a real
 * access-store assignment (`source: 'store'`, e.g. via `?dev_user=`).
 */
export function resolveAccess({ me, claimScope, dev }: IResolveInput): IAccessSnapshot | null {
  if (!me) return null;
  const fromMe = accessOfMe(me, claimScope);
  if (dev && fromMe.source !== 'store') return dev;
  return fromMe;
}

/** Does the snapshot hold `permission`? Exact key match, fail closed. */
export function holds(snapshot: IAccessSnapshot | null, permission: PermissionKey): boolean {
  return !!snapshot && !snapshot.noAccess && snapshot.permissions.includes(permission);
}
